import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IProjectOutputDto } from 'src/app/core/models/api/sane-project/arpa-model';
import { IProjectDraftDto } from 'src/app/features/sane-projects/pages/mappers/project-draft.dto';
import { ProjectViewModel } from 'src/app/features/sane-projects/pages/models/project.viewmodel';

const STORAGE_KEY_DRAFT = 'arpa.project.draft';
const STORAGE_KEY_CANSEETRACE = 'arpa.project.canSeeTrace';
const STORAGE_KEY_CANSEECOEGTI = 'arpa.project.canSeePmTICoeg';

@Injectable({ providedIn: 'root' })
export class SharedService {
  private projectSubject = new BehaviorSubject<ProjectViewModel | null>(null);
  public project$: Observable<ProjectViewModel | null> = this.projectSubject.asObservable();

  private dtoSubject = new BehaviorSubject<IProjectOutputDto | null>(null);
  public dto$: Observable<IProjectOutputDto | null> = this.dtoSubject.asObservable();

  private canSeeTraceSubject = new BehaviorSubject<boolean>(false);
  public canSeeTrace$: Observable<boolean> = this.canSeeTraceSubject.asObservable();

  private canSeeCoegTISubject = new BehaviorSubject<boolean>(false);
  public canSeePmTICoeg$: Observable<boolean> = this.canSeeCoegTISubject.asObservable();

  constructor() {
    try {
      const rawTrace = sessionStorage.getItem(STORAGE_KEY_CANSEETRACE);
      if (rawTrace !== null) {
        this.canSeeTraceSubject.next(rawTrace === '1');
      }
    } catch { /* noop */ }

    try {
      const rawCoeg = sessionStorage.getItem(STORAGE_KEY_CANSEECOEGTI);
      if (rawCoeg !== null) {
        this.canSeeCoegTISubject.next(rawCoeg === '1');
      }
    } catch { /* noop */ }
  }

  public getModel(): ProjectViewModel | null {
    return this.projectSubject.getValue();
  }

  public setModel(model: ProjectViewModel): void {
    this.projectSubject.next(model);
  }

  public clearModel(): void {
    this.projectSubject.next(null);
    this.clearDto();
    this.clearCanSeeTrace();
    this.clearCanSeeCoegTI();
  }

  public getDto(): IProjectOutputDto | null {
    return this.dtoSubject.getValue();
  }

  public setDto(dto: IProjectOutputDto): void {
    this.dtoSubject.next(dto);
  }

  public clearDto(): void {
    this.dtoSubject.next(null);
  }

  public setCanSeeTrace(canSeeTrace: boolean): void {
    this.canSeeTraceSubject.next(canSeeTrace);
    try {
      sessionStorage.setItem(STORAGE_KEY_CANSEETRACE, canSeeTrace ? '1' : '0');
    } catch { /* noop */ }
  }

  public getCanSeeTrace(): boolean {
    return this.canSeeTraceSubject.getValue();
  }

  public clearCanSeeTrace(): void {
    this.canSeeTraceSubject.next(false);
    try {
      sessionStorage.removeItem(STORAGE_KEY_CANSEETRACE);
    } catch { /* noop */ }
  }

  public setCanSeeCoegTI(canSeePmTICoeg: boolean): void {
    this.canSeeCoegTISubject.next(canSeePmTICoeg);
    try {
      sessionStorage.setItem(STORAGE_KEY_CANSEECOEGTI, canSeePmTICoeg ? '1' : '0');
    } catch { /* noop */ }
  }

  public getCanSeeCoegTI(): boolean {
    return this.canSeeCoegTISubject.getValue();
  }

  public clearCanSeeCoegTI(): void {
    this.canSeeCoegTISubject.next(false);
    try {
      sessionStorage.removeItem(STORAGE_KEY_CANSEECOEGTI);
    } catch { /* noop */ }
  }

  public saveDraft(draft: IProjectDraftDto | null): void {
    if (!draft) {
      sessionStorage.removeItem(STORAGE_KEY_DRAFT);
      return;
    }
    try {
      sessionStorage.setItem(STORAGE_KEY_DRAFT, JSON.stringify(draft));
    } catch { /* noop */ }
  }

  public loadDraft(): IProjectDraftDto | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_DRAFT);
      return raw ? (JSON.parse(raw) as IProjectDraftDto) : null;
    } catch {
      sessionStorage.removeItem(STORAGE_KEY_DRAFT);
      return null;
    }
  }

  public clearDraft(): void {
    sessionStorage.removeItem(STORAGE_KEY_DRAFT);
  }
}
